/**
 * Capability palette helpers — skills-anywhere rewrite.
 *
 * Non-component exports for the `/` skill completion path: the command-message
 * view model, the reload re-identification logic, and the live-eligibility
 * predicate that drives the `@localagent/ui` `/` trigger.
 *
 * These live in a plain `.ts` (NOT a `.tsx`) so the React components
 * (`CommandMessage`) live in sibling `.tsx` files.
 *
 * Boundary invariant (grep-verifiable acceptance criterion): these helpers call
 * ONLY pure logic over the SDK's capability types — never `sdk.tools.*`.
 */

import type { Capability } from '@localagent/sdk';

/** True for capabilities listed in the `/` completion picker: skills only.
 *  Setup (`workspace_init`) and other coded tools live on `#`. */
export function isPaletteListedCapability(cap: Capability): boolean {
  return cap.kind === 'skill';
}

export type CommandMessageStatus = 'pending' | 'success' | 'error';

export interface CommandMessageView {
  id: string;
  toolName: string;
  args: Record<string, unknown>;
  status: CommandMessageStatus;
  preview?: string;
  errorKind?: 'execute_failed' | 'persist_failed_after_execution';
  error?: string;
}

/**
 * Tools whose `content`/`preview` is a JSON envelope rather than a bounded
 * preview string. The discriminator is BY TOOL
 * NAME — a name in this set is parsed as JSON; every other coded result is
 * rendered as the preview string. No `/init`- or skill-specific render code
 * beyond this allow-list.
 */
export const JSON_ENVELOPE_TOOL_NAMES = new Set<string>(['workspace_init']);

export interface StoredThreadMessage {
  id: string;
  role: string;
  content: string;
  tool_calls?: Array<{
    id: string;
    function: { name: string; arguments: string };
  }>;
  tool_call_id?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  thinking_content?: string | null;
  entry_type?: string;
  attachment_path?: string | null;
}

export type ReidentifiedItem =
  | { kind: 'message'; message: StoredThreadMessage }
  | { kind: 'command'; view: CommandMessageView };

/**
 * Collapse persisted user-invoked pairs back into command messages. A stored
 * assistant entry carrying `tool_calls` in the `'session'` lane is uniquely a
 * user-invoked pair (agent tool rows only land in per-run task lanes, and
 * `getMessages` already returns only the session lane) — so it is rendered as
 * the same command message, paired with its matching `role:"tool"` result by
 * `tool_call_id`, never leaked as a raw assistant/tool message.
 */
export function reidentifyCommandMessages(
  messages: StoredThreadMessage[],
): ReidentifiedItem[] {
  const consumed = new Set<string>();
  const items: ReidentifiedItem[] = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (consumed.has(msg.id)) continue;

    const isCommandCall =
      msg.role === 'assistant' &&
      Array.isArray(msg.tool_calls) &&
      msg.tool_calls.length > 0;

    if (isCommandCall) {
      const call = msg.tool_calls![0];
      const result = messages
        .slice(i + 1)
        .find((m) => m.role === 'tool' && m.tool_call_id === call.id);
      if (result) consumed.add(result.id);

      let args: Record<string, unknown> = {};
      let parseError: string | undefined;
      try {
        if (call.function.arguments) {
          args = JSON.parse(call.function.arguments) as Record<string, unknown>;
        }
      } catch (err) {
        parseError = err instanceof Error ? err.message : String(err);
      }

      const view: CommandMessageView = {
        id: msg.id,
        toolName: call.function.name,
        args,
        status: parseError ? 'error' : 'success',
        error: parseError,
      };
      if (result) view.preview = result.content;
      items.push({ kind: 'command', view });
      continue;
    }

    if (msg.role === 'tool') continue;
    items.push({ kind: 'message', message: msg });
  }

  return items;
}
